import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Campaign {
  id: number;
  name: string;
}

const ShipmentsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [selectedCampaign, setSelectedCampaign] = useState<number | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [customerMatchField, setCustomerMatchField] = useState('');
  const [contactField, setContactField] = useState('');
  const [accountId, setAccountId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [matching, setMatching] = useState(false);

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const fetchCampaigns = React.useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      setCampaigns([]); // Set empty array on error
    }
  }, [API_BASE]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleShipmentUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !customerMatchField) {
      alert('Please select a CSV file and specify the customer match field');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('customerMatchField', customerMatchField);
    if (accountId) {
      formData.append('accountId', accountId);
    }

    try {
      const response = await axios.post(`${API_BASE}/shipments/import`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      alert(`Successfully imported ${response.data.imported} shipments`);
      setFile(null);
    } catch (error) {
      console.error('Error uploading shipments:', error);
      alert('Error uploading shipments');
    } finally {
      setUploading(false);
    }
  };

  const handleMatchToCampaign = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCampaign || !contactField) {
      alert('Please select a campaign and specify the contact field to match');
      return;
    }

    setMatching(true);
    try {
      const response = await axios.post(`${API_BASE}/shipments/match-to-campaign`, {
        campaignId: selectedCampaign,
        contactField
      });
      alert(`Successfully matched ${response.data.matched} shipments to contacts`);
    } catch (error) {
      console.error('Error matching shipments:', error);
      alert('Error matching shipments to contacts');
    } finally {
      setMatching(false);
    }
  };

  return (
    <div>
      <h1 className="page-title">Shipments Management</h1>

      <div className="card">
        <h3>Import Shipments from Stamps.com</h3>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          Currently supports CSV export from Stamps.com. Direct API integration coming soon.
        </p>
        
        <form onSubmit={handleShipmentUpload}>
          <div className="form-group">
            <label htmlFor="accountId">Stamps.com Account ID (Optional)</label>
            <input
              type="text"
              id="accountId"
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              placeholder="Your Stamps.com account identifier"
            />
          </div>

          <div className="form-group">
            <label htmlFor="customerMatchField">Customer Match Field</label>
            <input
              type="text"
              id="customerMatchField"
              value={customerMatchField}
              onChange={(e) => setCustomerMatchField(e.target.value)}
              placeholder="e.g., CustomerEmail, OrderID, CustomerName"
              required
            />
            <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
              The CSV column that contains the value to match against contact data
            </small>
          </div>
          
          <div className="form-group">
            <label htmlFor="shipmentFile">Shipments CSV File</label>
            <input
              type="file"
              id="shipmentFile"
              accept=".csv"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              required
            />
            <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
              Export from Stamps.com with columns like ShipmentID, TrackingNumber, Status, ShipDate, etc.
            </small>
          </div>

          <button type="submit" className="btn" disabled={uploading}>
            {uploading ? 'Uploading...' : 'Import Shipments'}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>Match Shipments to Campaign Contacts</h3>
        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
          After importing shipments and contacts, match them based on a common field.
        </p>
        
        <form onSubmit={handleMatchToCampaign}>
          <div className="form-group">
            <label htmlFor="campaign">Select Campaign</label>
            <select
              id="campaign"
              value={selectedCampaign || ''}
              onChange={(e) => setSelectedCampaign(Number(e.target.value) || null)}
              required
            >
              <option value="">Select a campaign...</option>
              {campaigns.map(campaign => (
                <option key={campaign.id} value={campaign.id}>
                  {campaign.name}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="contactField">Contact Field to Match</label>
            <input
              type="text"
              id="contactField"
              value={contactField}
              onChange={(e) => setContactField(e.target.value)}
              placeholder="e.g., Email, Order ID, Customer Name"
              required
            />
            <small style={{ color: '#666', display: 'block', marginTop: '0.5rem' }}>
              The field in your contact data that corresponds to the customer match field in shipments
            </small>
          </div>

          <button type="submit" className="btn" disabled={matching || !selectedCampaign}>
            {matching ? 'Matching...' : 'Match Shipments to Contacts'}
          </button>
        </form>
      </div>

      <div className="card">
        <h3>Stamps.com Integration Guide</h3>
        <div style={{ background: '#f8f9fa', padding: '1.5rem', borderRadius: '4px' }}>
          <h4>Current Workflow (CSV Import)</h4>
          <ol style={{ marginLeft: '1.5rem', marginBottom: '1rem' }}>
            <li>Export your shipment data from Stamps.com as a CSV file</li>
            <li>Ensure the CSV includes columns like ShipmentID, TrackingNumber, Status, ShipDate</li>
            <li>Include a customer identifier column (email, order ID, etc.)</li>
            <li>Upload the CSV using the form above</li>
            <li>Match shipments to your campaign contacts</li>
          </ol>

          <h4>Direct API Integration (Coming Soon)</h4>
          <p style={{ color: '#666', margin: '0.5rem 0' }}>
            We're working on direct integration with the Stamps.com API to automatically fetch shipment data.
            This will eliminate the need for manual CSV exports and provide real-time shipment status updates.
          </p>

          <h4>Expected CSV Columns</h4>
          <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
            <li><strong>ShipmentID / ID / OrderID:</strong> Unique shipment identifier</li>
            <li><strong>TrackingNumber / Tracking:</strong> Package tracking number</li>
            <li><strong>Status:</strong> Shipment status (e.g., "Shipped", "Delivered")</li>
            <li><strong>ShipDate:</strong> Date the package was shipped</li>
            <li><strong>Customer Match Field:</strong> Your chosen field to match contacts (email, order ID, etc.)</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ShipmentsPage;
